/**
 * ConversationHistoryTimelinePremium - Timeline premium de movimentações
 * 
 * Design moderno baseado em referência visual com:
 * - Ícones grandes em quadrados arredondados
 * - Linha vertical com pontos de conexão circulares
 * - Cards elevados com sombra suave
 * - Cores vibrantes por tipo de ação
 * 
 * RELEASE: v1.0.0 - 2026-01-08
 */

import {
  Plus,
  MessageCircle,
  CheckCircle,
  AlertTriangle,
  Clock,
  ArrowRightLeft,
  Building,
  Bot,
  BotOff,
  UserCheck,
  RefreshCw,
  History,
  Workflow,
  Webhook,
  Cog,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { useConversationActionLogs, ConversationActionType } from '@/hooks/useConversationActionLogs';
import type { LucideIcon } from 'lucide-react';

/**
 * Mapeia a "origin" das ações automáticas (vindas em details.origin)
 * para um ícone visual + label exibido ao lado do avatar do executor.
 */
function getSystemBadge(origin?: string | null): { Icon: LucideIcon; label: string } {
  switch (origin) {
    case 'auto_expire':
      return { Icon: Clock, label: 'Expiração automática' };
    case 'flow':
      return { Icon: Workflow, label: 'Fluxo automático' };
    case 'webhook':
      return { Icon: Webhook, label: 'Contato iniciou' };
    default:
      return { Icon: Cog, label: 'Sistema' };
  }
}

// Props do componente
interface ConversationHistoryTimelinePremiumProps {
  conversationId: string | null;
  isActive?: boolean;
}

// Mapeamento de ícones por tipo de ação
const actionIcons: Record<ConversationActionType, LucideIcon> = {
  conversation_created: Plus,
  status_to_open: MessageCircle,
  status_to_closed: CheckCircle,
  status_to_spam: AlertTriangle,
  status_to_pending: Clock,
  transfer_agent: ArrowRightLeft,
  transfer_department: Building,
  transfer: ArrowRightLeft,
  bot_paused: BotOff,
  bot_resumed: Bot,
  agent_assigned: UserCheck,
};

// Cores vibrantes para containers de ícones (bg mais leve, texto mais escuro)
const actionColors: Record<ConversationActionType, { bg: string; text: string; border: string }> = {
  conversation_created: { 
    bg: 'bg-emerald-100 dark:bg-emerald-900/30', 
    text: 'text-emerald-600 dark:text-emerald-400',
    border: 'border-emerald-200 dark:border-emerald-700'
  },
  status_to_open: { 
    bg: 'bg-blue-100 dark:bg-blue-900/30', 
    text: 'text-blue-600 dark:text-blue-400',
    border: 'border-blue-200 dark:border-blue-700'
  },
  status_to_closed: { 
    bg: 'bg-slate-100 dark:bg-slate-800/50', 
    text: 'text-slate-600 dark:text-slate-400',
    border: 'border-slate-200 dark:border-slate-600'
  },
  status_to_spam: { 
    bg: 'bg-orange-100 dark:bg-orange-900/30', 
    text: 'text-orange-600 dark:text-orange-400',
    border: 'border-orange-200 dark:border-orange-700'
  },
  status_to_pending: { 
    bg: 'bg-yellow-100 dark:bg-yellow-900/30', 
    text: 'text-yellow-600 dark:text-yellow-400',
    border: 'border-yellow-200 dark:border-yellow-700'
  },
  transfer_agent: { 
    bg: 'bg-indigo-100 dark:bg-indigo-900/30', 
    text: 'text-indigo-600 dark:text-indigo-400',
    border: 'border-indigo-200 dark:border-indigo-700'
  },
  transfer_department: { 
    bg: 'bg-purple-100 dark:bg-purple-900/30', 
    text: 'text-purple-600 dark:text-purple-400',
    border: 'border-purple-200 dark:border-purple-700'
  },
  transfer: { 
    bg: 'bg-indigo-100 dark:bg-indigo-900/30', 
    text: 'text-indigo-600 dark:text-indigo-400',
    border: 'border-indigo-200 dark:border-indigo-700'
  },
  bot_paused: { 
    bg: 'bg-red-100 dark:bg-red-900/30', 
    text: 'text-red-600 dark:text-red-400',
    border: 'border-red-200 dark:border-red-700'
  },
  bot_resumed: { 
    bg: 'bg-green-100 dark:bg-green-900/30', 
    text: 'text-green-600 dark:text-green-400',
    border: 'border-green-200 dark:border-green-700'
  },
  agent_assigned: { 
    bg: 'bg-cyan-100 dark:bg-cyan-900/30', 
    text: 'text-cyan-600 dark:text-cyan-400',
    border: 'border-cyan-200 dark:border-cyan-700'
  },
};

// Labels por tipo de ação
const actionLabels: Record<ConversationActionType, string> = {
  conversation_created: 'Conversa iniciada',
  status_to_open: 'Conversa em atendimento',
  status_to_closed: 'Conversa encerrada',
  status_to_spam: 'Marcada como spam',
  status_to_pending: 'Marcada como pendente',
  transfer_agent: 'Transferência para agente',
  transfer_department: 'Transferência para departamento',
  transfer: 'Transferência',
  bot_paused: 'Bot pausado',
  bot_resumed: 'Bot reativado',
  agent_assigned: 'Agente atribuído',
};

// Status labels
const statusLabels: Record<string, string> = {
  open: 'Em atendimento',
  closed: 'Encerrada',
  spam: 'Spam',
  pending: 'Pendente',
};

/**
 * Componente Timeline Premium para histórico de conversas
 */
export function ConversationHistoryTimelinePremium({
  conversationId,
  isActive = true,
}: ConversationHistoryTimelinePremiumProps) {
  // Hook para buscar logs de ações
  const { logs, isLoading, error } = useConversationActionLogs(conversationId, {
    enabled: isActive && !!conversationId,
  });

  /**
   * Retorna o label do performer baseado no origin quando não há nome
   * Exibe "Sistema", "Expiração automática", etc. para ações automáticas
   */
  const getPerformerLabel = (log: typeof logs[0]): string | null => {
    // Se tem nome do performer, retorna ele
    if (log.performer_name) {
      return log.performer_name;
    }
    
    // Verifica origem nos details para ações automáticas
    const origin = log.details?.origin as string | undefined;
    switch (origin) {
      case 'auto_expire':
        return 'Expiração automática';
      case 'flow':
        return 'Fluxo automático';
      case 'webhook':
        return 'Contato iniciou';
      case 'manual':
        return null; // Deveria ter performer_name, mas fallback
      default:
        // Para logs antigos sem origin, não exibe nada
        return null;
    }
  };

  /**
   * Gera descrição detalhada para cada tipo de ação
   */
  const getActionDescription = (log: typeof logs[0]): string | null => {
    const details = log.details || {};

    switch (log.action_type) {
      case 'status_to_open':
      case 'status_to_closed':
      case 'status_to_spam':
      case 'status_to_pending': {
        const prevStatus = statusLabels[details.previous_status as string] || details.previous_status;
        const newStatus = statusLabels[details.new_status as string] || details.new_status;
        return prevStatus ? `${prevStatus} → ${newStatus}` : null;
      }

      case 'transfer_agent':
        return log.to_agent_name ? `→ ${log.to_agent_name}` : null;

      case 'transfer_department':
        return log.to_department_name ? `→ ${log.to_department_name}` : null;

      case 'bot_paused': {
        const pausedUntil = details.paused_until as string | null;
        if (pausedUntil) {
          return `Pausado até ${format(new Date(pausedUntil), "dd/MM 'às' HH:mm", { locale: ptBR })}`;
        }
        return null;
      }

      case 'agent_assigned':
        return log.to_agent_name ? `→ ${log.to_agent_name}` : null;

      default:
        return null;
    }
  };

  // Estado de loading com skeletons premium
  if (isLoading) {
    return (
      <div className="space-y-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex gap-4">
            <Skeleton className="h-12 w-12 rounded-xl flex-shrink-0" />
            <div className="flex-1 space-y-2 pt-1">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-3 w-28" />
              <Skeleton className="h-3 w-48" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Estado de erro
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <div className="h-14 w-14 rounded-xl bg-destructive/10 flex items-center justify-center mb-4">
          <AlertTriangle className="h-7 w-7 text-destructive" />
        </div>
        <p className="text-sm font-medium">Erro ao carregar histórico</p>
        <p className="text-xs text-muted-foreground/70 mt-1">Tente novamente mais tarde</p>
      </div>
    );
  }

  // Estado vazio
  if (logs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <div className="h-14 w-14 rounded-xl bg-muted flex items-center justify-center mb-4">
          <History className="h-7 w-7 opacity-50" />
        </div>
        <p className="text-sm font-medium">Nenhuma movimentação</p>
        <p className="text-xs text-muted-foreground/70 mt-1">O histórico aparecerá aqui</p>
      </div>
    );
  }

  // Timeline premium
  return (
    <div className="relative">
      {/* Linha vertical de conexão */}
      <div className="absolute left-6 top-6 bottom-6 w-0.5 bg-gradient-to-b from-border via-border to-transparent" />

      <div className="space-y-5">
        {logs.map((log, index) => {
          const Icon = actionIcons[log.action_type] || RefreshCw;
          const colors = actionColors[log.action_type] || { 
            bg: 'bg-muted', 
            text: 'text-muted-foreground',
            border: 'border-border'
          };
          const label = actionLabels[log.action_type] || log.action_type;
          const description = getActionDescription(log);
          const performerLabel = getPerformerLabel(log);
          const isLast = index === logs.length - 1;

          return (
            <div key={log.id} className="relative flex gap-4">
              {/* Container do ícone (quadrado arredondado) */}
              <div 
                className={cn(
                  "relative z-10 h-12 w-12 rounded-xl flex items-center justify-center flex-shrink-0",
                  "shadow-sm border",
                  colors.bg,
                  colors.border
                )}
              >
                <Icon className={cn("h-5 w-5", colors.text)} />
              </div>

              {/* Ponto de conexão na linha vertical */}
              {!isLast && (
                <div className="absolute left-[22px] top-14 w-3 h-3 rounded-full bg-background border-2 border-muted z-10" />
              )}

              {/* Card de conteúdo */}
              <div 
                className={cn(
                  "flex-1 min-w-0 bg-card rounded-xl p-4",
                  "border border-border/50 shadow-sm",
                  "hover:shadow-md transition-shadow duration-200"
                )}
              >
                {/* Título da ação */}
                <h4 className="text-sm font-bold text-foreground">
                  {label}
                </h4>
                
                {/* Descrição detalhada */}
                {description && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {description}
                  </p>
                )}

                {/* Metadata: data, hora e executor (avatar humano OU ícone de sistema) */}
                <div className="flex items-center gap-2 flex-wrap mt-3 text-xs text-muted-foreground/70">
                  <span>
                    {format(new Date(log.created_at), "dd/MM", { locale: ptBR })}
                  </span>
                  <span className="text-muted-foreground/40">-</span>
                  <span>
                    às {format(new Date(log.created_at), "HH:mm", { locale: ptBR })}
                  </span>

                  {performerLabel && (
                    <>
                      <span className="text-muted-foreground/40">-</span>
                      {/* Executor humano: avatar + nome */}
                      {log.performed_by ? (
                        <span className="flex items-center gap-1.5">
                          <SmartAvatar
                            size="xs"
                            src={log.performer_avatar}
                            name={log.performer_name}
                          />
                          <span className="font-medium text-muted-foreground">
                            {performerLabel}
                          </span>
                        </span>
                      ) : (
                        // Ação do sistema: ícone identificador + label
                        (() => {
                          const origin = (log.details?.origin as string | undefined) ?? null;
                          const { Icon: SysIcon } = getSystemBadge(origin);
                          return (
                            <span className="flex items-center gap-1.5">
                              <span
                                className="h-6 w-6 rounded-full bg-muted flex items-center justify-center"
                                title="Ação do sistema"
                              >
                                <SysIcon className="h-3 w-3 text-muted-foreground" />
                              </span>
                              <span className="font-medium text-muted-foreground">
                                {performerLabel}
                              </span>
                            </span>
                          );
                        })()
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
